import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { formatQueryIntoURLQueryParams } from '../../../../utils/listing-query-helper';
import { searchFilterIds } from '../../../../constants/test-constants';

import type SavedSearch from '../../../../data/saved-searches';
import type { GetUserLastSearchResponse } from '../../../../pages/api/last-search/types';
import type { MenuItem } from '@core/ui/navigation';

dayjs.extend(relativeTime);

export interface SavedSearchesNavConfig {
  myAccountLabel: string; // e.g., "My Zoocasa", "My eXp", etc.
  viewAllSearchesPath: string;
  myAccountPath: string;
}

/**
 * Maps a saved search object to a navigation item.
 * 
 * @param search - The saved search object to map
 * @param index - The index of the search in the list (used for unique ID)
 * @returns Navigation item configuration for the saved search
 */
const mapSavedSearchToNavItem = (search: SavedSearch, index: number): MenuItem => ({
  id: `saved-search-${index}`,
  type: 'link',
  label: search.description,
  secondaryLabel: dayjs(search.createdAt).fromNow(),
  href: search.path,
});

/**
 * Maps a last search response to a navigation item.
 * 
 * @param lastSearch - The last search data to map
 * @returns Navigation item configuration for the last search
 */
const mapLastSearchToNavItem = (lastSearch: GetUserLastSearchResponse): MenuItem => {
  const lastSearchPath = `/search?${formatQueryIntoURLQueryParams(lastSearch.lastSearch.filter)}`;
  return {
    id: 'last-search',
    type: 'link',
    label: 'My Last Search',
    secondaryLabel: dayjs(lastSearch.lastUpdated).fromNow(),
    href: lastSearchPath,
  };
};

/**
 * Creates the footer navigation items (View All, Account links) based on configuration.
 * 
 * @param config - Configuration object with labels and paths
 * @returns Array of footer navigation items
 */
const createFooterNavItems = (config: SavedSearchesNavConfig): MenuItem[] => [
  {
    id: 'view-all-searches',
    type: 'link',
    label: 'View All Saved Searches',
    href: config.viewAllSearchesPath,
    testId: searchFilterIds.viewAllSavedSearches,
  },
  {
    id: 'divider-1',
    type: 'divider',
  },
  {
    id: 'my-account',
    type: 'link',
    label: config.myAccountLabel,
    href: config.myAccountPath,
    testId: searchFilterIds.myZoocasa,
  },
];

/**
 * Creates the default "create first search" navigation item.
 * 
 * @returns Navigation item configuration for creating first search
 */
const createDefaultNavItem = (): MenuItem => ({
  id: 'create-first',
  type: 'link',
  label: 'Create your first search',
  href: '/search',
});

/**
 * Transforms saved searches and last search data into navigation menu items for the header dropdown.
 * 
 * This function orchestrates the creation of a structured navigation menu using dedicated mappers.
 * 
 * @param savedSearches - Array of user's saved search objects
 * @param lastSearch - Optional user's most recent search data with filter and timestamp
 * @param config - Optional configuration object to customize labels and paths for different tenants
 * 
 * @returns Array of navigation item configurations ready for use in the DesktopNavMenu component
 * 
 * @example
 * ```typescript
 * const navItems = transformSavedSearchesToNavItems(
 *   savedSearches,
 *   lastSearch,
 *   { myAccountLabel: 'My eXp', viewAllSearchesPath: '/account/searches', myAccountPath: '/account' }
 * );
 * ```
 */
export const transformSavedSearchesToNavItems = (
  savedSearches: SavedSearch[], 
  lastSearch?: GetUserLastSearchResponse,
  config?: SavedSearchesNavConfig
): MenuItem[] => {
  // Default configuration for backwards compatibility
  const defaultConfig: SavedSearchesNavConfig = {
    myAccountLabel: 'My Zoocasa',
    viewAllSearchesPath: '/account/saved/searches',
    myAccountPath: '/account/saved/properties',
  };

  const finalConfig = { ...defaultConfig, ...config };
  const items: MenuItem[] = [];

  // Map last search if available
  if (lastSearch?.lastSearch?.filter) {
    items.push(mapLastSearchToNavItem(lastSearch));
  }

  // Map saved searches (limit to 3)
  const savedSearchesToShow = savedSearches.slice(0, 3);
  const mappedSavedSearches = savedSearchesToShow.map(mapSavedSearchToNavItem);
  items.push(...mappedSavedSearches);

  // Add footer items if we have any searches
  if (savedSearchesToShow.length > 0 || lastSearch) {
    items.push(...createFooterNavItems(finalConfig));
  }

  // Default state when no searches
  if (items.length === 0) {
    items.push(createDefaultNavItem());
  }

  return items;
}; 